/**
 * The hard gate.
 *
 * Without the `admin` claim this app renders **one page and nothing else** — no shell, no
 * nav, no query, no listener. Not a redirect to the Studio either: a person who lands here
 * without the claim has arrived at the right door with the wrong key, and telling them that
 * plainly is more use than bouncing them somewhere they did not ask for.
 *
 * The gate sits *above* `Outlet`, so a surface's data hooks never mount for a non-admin.
 * Every read below this point is one the rules would refuse anyway; this is the version of
 * that refusal a person can read.
 */

import { Button, Card, Wordmark } from '@atlas-nook/design-system';
import type { JSX } from 'react';
import { Navigate, useLocation } from 'react-router';

import { ADMIN_STRINGS as S } from '../strings.js';
import { AdminShell } from '../shell/AdminShell.js';
import { useAuth } from './AuthProvider.js';

export function RequireAdmin(): JSX.Element {
  const location = useLocation();
  const { session, signOutNow } = useAuth();

  if (session === undefined) {
    return (
      <p className="note" style={{ padding: 'var(--sp-6)' }} role="status">
        {S.gate.opening}
      </p>
    );
  }

  if (session === null) {
    return <Navigate to="/signin" replace state={{ from: location.pathname }} />;
  }

  if (!session.admin) return <NotAnAdmin email={session.email} onSignOut={signOutNow} />;

  return <AdminShell />;
}

/** The whole app, for an account without the claim. */
function NotAnAdmin({
  email,
  onSignOut,
}: {
  email: string;
  onSignOut: () => Promise<void>;
}): JSX.Element {
  return (
    <main className="surface surface-center" id="main" style={{ minHeight: '100vh' }}>
      <Card raised>
        <div style={{ display: 'grid', gap: 'var(--sp-5)', maxWidth: '46ch' }}>
          <Wordmark descriptor="admin" />
          <h1 className="story" style={{ fontSize: 'var(--t-d2)' }}>
            {S.gate.notAdminTitle}
          </h1>
          <p className="note">{S.gate.notAdminBody}</p>
          <p className="cap">{S.gate.notAdminWho(email)}</p>
          <div className="btnrow">
            <Button tone="secondary" onClick={() => void onSignOut()}>
              {S.gate.signOut}
            </Button>
          </div>
        </div>
      </Card>
    </main>
  );
}
